import React, { useState, useEffect, useCallback, useMemo } from "react";
import "./ShowLeads.css";
import axios from "axios";
import API_URL from "../../../utils/API_URL";
import { useNavigate } from "react-router-dom";
import brandLogo from "../../assets/Logo+-+livvium.png";

const ShowLeads = () => {
  const [leadsData, setLeadsData] = useState({
    preferences: [],
    page: 1,
    pageSize: 10,
    totalPages: 1,
    total: 0,
  });

  const [uiState, setUiState] = useState({
    loading: true,
    error: null,
    selectedLead: null,
    showDetailModal: false,
    currentPage: 1,
    searchTerm: "",
    sortBy: "createdAt",
    sortOrder: "desc",
    // updated filter keys to match backend buildFilter
    filterBy: {
      userType: "",
      brandInterest: "",
      creatorInterest: "",
      partnerInterest: "",
      timeline: "",
    },
    showFilters: false,
    selectedLeads: new Set(),
    showStats: true,
    dateRange: {
      startDate: "",
      endDate: "",
    },
  });

  const navigate = useNavigate();

  // Build query parameters for backend
  const buildQueryParams = useCallback(
    (page = 1, customFilters = null) => {
      const params = new URLSearchParams();

      // Pagination
      params.append("page", page);
      params.append("pageSize", leadsData.pageSize);

      // Sorting
      params.append("sortBy", uiState.sortBy);
      params.append("sortOrder", uiState.sortOrder);

      // Use custom filters if provided, otherwise use current state
      const filters = customFilters?.filterBy ?? uiState.filterBy;
      const searchTerm = customFilters?.searchTerm ?? uiState.searchTerm;
      const dateRange = customFilters?.dateRange ?? uiState.dateRange;

      // Search term -> server expects "search"
      if (searchTerm) params.append("search", searchTerm);

      // Exact-match filters used by backend
      if (filters.userType) params.append("userType", filters.userType);
      if (filters.brandInterest) params.append("brandInterest", filters.brandInterest);
      if (filters.creatorInterest) params.append("creatorInterest", filters.creatorInterest);
      if (filters.partnerInterest) params.append("partnerInterest", filters.partnerInterest);
      if (filters.timeline) params.append("timeline", filters.timeline);

      // Date range
      if (dateRange.startDate) params.append("startDate", dateRange.startDate);
      if (dateRange.endDate) params.append("endDate", dateRange.endDate);

      return params.toString();
    },
    [
      leadsData.pageSize,
      uiState.sortBy,
      uiState.sortOrder,
      uiState.filterBy,
      uiState.searchTerm,
      uiState.dateRange,
    ]
  );

  // Fetch leads data with backend filtering
  const fetchLeads = useCallback(
    async (page = 1, customFilters = null) => {
      setUiState((prev) => ({ ...prev, loading: true, error: null }));

      try {
        const token = localStorage.getItem("token");
        const queryString = buildQueryParams(page, customFilters);

        const response = await axios.get(
          `${API_URL}/admin/getAllUsersPreferences?${queryString}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.data.status) {
          setLeadsData(response.data.data);
          setUiState((prev) => ({ ...prev, currentPage: page }));
        }
      } catch (error) {
        if (error.response.status === 401) {
          console.log("=====================");
          navigate("/adminlogin");
        }
        setUiState((prev) => ({
          ...prev,
          error: error.response?.data?.message || "Failed to fetch leads data",
        }));
      } finally {
        setUiState((prev) => ({ ...prev, loading: false }));
      }
    },
    [buildQueryParams]
  );

  // Debounced search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchLeads(1);
    }, 500); // 500ms delay for search

    return () => clearTimeout(timeoutId);
  }, [uiState.searchTerm]);

  // Fetch on filter changes
  useEffect(() => {
    fetchLeads(1);
  }, [uiState.filterBy, uiState.sortBy, uiState.sortOrder, uiState.dateRange]);

  // Initial load
  useEffect(() => {
    fetchLeads(1);
  }, []);

  // Statistics - now mirror userType/timeline counts
  const stats = useMemo(() => {
    const preferences = leadsData.preferences || [];
    return {
      total: leadsData.total,
      currentPage: preferences.length,
      brands: preferences.filter((p) => p.userType === "Brand / Retailer").length,
      creators: preferences.filter((p) => p.userType === "Creator / Designer").length,
      partners: preferences.filter((p) => p.userType === "Partner / Platform").length,
      browsing: preferences.filter((p) => p.userType === "Just browsing").length,
      asapTimeline: preferences.filter((p) => p.timeline === "Already exploring solutions").length,
    };
  }, [leadsData]);

  // Event handlers
  const handleSearch = (e) => {
    setUiState((prev) => ({ ...prev, searchTerm: e.target.value }));
  };

  const handleSort = (field) => {
    setUiState((prev) => ({
      ...prev,
      sortBy: field,
      sortOrder:
        prev.sortBy === field && prev.sortOrder === "desc" ? "asc" : "desc",
    }));
  };

  const handleFilterChange = (filterType, value) => {
    setUiState((prev) => ({
      ...prev,
      filterBy: {
        ...prev.filterBy,
        [filterType]: value,
      },
    }));
  };

  const handleDateRangeChange = (field, value) => {
    setUiState((prev) => ({
      ...prev,
      dateRange: {
        ...prev.dateRange,
        [field]: value,
      },
    }));
  };

  // const handleLeadSelect = (leadId) => {
  //   setUiState((prev) => {
  //     const newSelected = new Set(prev.selectedLeads);
  //     if (newSelected.has(leadId)) {
  //       newSelected.delete(leadId);
  //     } else {
  //       newSelected.add(leadId);
  //     }
  //     return { ...prev, selectedLeads: newSelected };
  //   });
  // };

  // const handleSelectAll = () => {
  //   setUiState((prev) => ({
  //     ...prev,
  //     selectedLeads:
  //       prev.selectedLeads.size === leadsData.preferences.length
  //         ? new Set()
  //         : new Set(leadsData.preferences.map((lead) => lead._id)),
  //   }));
  // };

  const showLeadDetails = (lead) => {
    setUiState((prev) => ({
      ...prev,
      selectedLead: lead,
      showDetailModal: true,
    }));
  };

  const closeDetailModal = () => {
    setUiState((prev) => ({
      ...prev,
      selectedLead: null,
      showDetailModal: false,
    }));
  };

  const clearAllFilters = () => {
    setUiState((prev) => ({
      ...prev,
      filterBy: {
        userType: "",
        brandInterest: "",
        creatorInterest: "",
        partnerInterest: "",
        timeline: "",
      },
      dateRange: {
        startDate: "",
        endDate: "",
      },
      searchTerm: "",
    }));
  };

  const exportLeads = async () => {
    try {
      const token = localStorage.getItem("token");
      const queryString = buildQueryParams(1, {
        ...uiState.filterBy,
        searchTerm: uiState.searchTerm,
        dateRange: uiState.dateRange,
      });

      const response = await axios.get(
        `${API_URL}/admin/exportUsersPreferences?${queryString}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          responseType: "blob",
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `leads_export_${new Date().toISOString().split("T")[0]}.csv`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setUiState((prev) => ({
        ...prev,
        error: "Failed to export data",
      }));
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getBadgeColor = (type, value) => {
    if (!value) return "gray";
    const v = String(value).toLowerCase();
    if (type === "userType") {
      if (v.includes("brand")) return "pink";
      if (v.includes("creator")) return "purple";
      if (v.includes("partner")) return "blue";
      if (v.includes("just browsing")) return "muted";
      return "gray";
    }
    if (type === "timeline") {
      if (v.includes("already")) return "red";
      if (v.includes("3")) return "orange";
      return "gray";
    }
    return "gray";
  };

  // Normalize backend documents to a single shape so we can show consistent fields
  const normalizeLead = (lead) => {
    if (!lead) return {};
    return {
      id: lead._id || lead.id || null,
      user: lead.user || null,
      firstName: lead.firstName || "",
      lastName: lead.lastName || "",
      email: lead.email || "",
      company: lead.company || "",
      phone: lead.phone || "",
      // keep only relevant fields present in your model
      userType: lead.userType || "",
      userTypeOther: lead.userTypeOther || "",
      brandInterest: lead.brandInterest || "",
      brandInterestOther: lead.brandInterestOther || "",
      creatorInterest: lead.creatorInterest || "",
      creatorInterestOther: lead.creatorInterestOther || "",
      partnerInterest: lead.partnerInterest || "",
      partnerInterestOther: lead.partnerInterestOther || "",
      timeline: lead.timeline || "",
      timelineOther: lead.timelineOther || "",
      createdAt: lead.createdAt,
      updatedAt: lead.updatedAt,
      raw: lead,
    };
  };

  // Get unique values for filter dropdowns (use normalized leads)
  const getUniqueValues = (field) => {
    const preferences = leadsData.preferences || [];
    const values = new Set();

    preferences.forEach((pref) => {
      const n = normalizeLead(pref);
      if (n[field]) values.add(n[field]);
    });

    return Array.from(values).filter(Boolean).sort();
  };

  if (uiState.loading && leadsData.preferences.length === 0) {
    return (
      <div className="show_leads_environment">
        <div className="show_leads_loading">
          <div className="show_leads_loader">
            <div className="show_leads_spinner"></div>
            <span>Loading leads data...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="show_leads_environment">
      {/* Header Section */}
      <div className="show_leads_header">
        <div className="show_leads_title_section">
          <h1 className="show_leads_title">
            <img src={brandLogo} alt="" />
            Leads Management
          </h1>
          <p className="show_leads_subtitle">
            Manage and analyze customer preferences and leads
          </p>
        </div>

        <div className="show_leads_header_actions">
          <button
            className="show_leads_export_btn"
            onClick={() => navigate("/adminSponsorship")}
          >
            <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
              {/* <!-- Handshake icon --> */}
              <path d="M1,12 C1,11.448 1.448,11 2,11 H7.586 L6.293,9.707 C5.902,9.316 5.902,8.684 6.293,8.293 C6.684,7.902 7.316,7.902 7.707,8.293 L10.707,11.293 C10.894,11.48 11,11.734 11,12 C11,12.266 10.894,12.52 10.707,12.707 L7.707,15.707 C7.316,16.098 6.684,16.098 6.293,15.707 C5.902,15.316 5.902,14.684 6.293,14.293 L7.586,13 H2 C1.448,13 1,12.552 1,12 Z M23,12 C23,11.448 22.552,11 22,11 H16.414 L17.707,9.707 C18.098,9.316 18.098,8.684 17.707,8.293 C17.316,7.902 16.684,7.902 16.293,8.293 L13.293,11.293 C13.106,11.48 13,11.734 13,12 C13,12.266 13.106,12.52 13.293,12.707 L16.293,15.707 C16.684,16.098 17.316,16.098 17.707,15.707 C18.098,15.316 18.098,14.684 17.707,14.293 L16.414,13 H22 C22.552,13 23,12.552 23,12 Z" />
            </svg>
            Go to Partnership
          </button>

          <button
            className="show_leads_refresh_btn"
            onClick={() => fetchLeads(uiState.currentPage)}
            disabled={uiState.loading}
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.65 6.35A7.958 7.958 0 0 0 12 4C8.69 4 5.83 6.22 4.78 9.4L2.74 8.75A10.958 10.958 0 0 1 12 2C15.31 2 18.24 3.65 20.24 6.34L22 4.58V10H16.42L17.65 6.35ZM6.35 17.65A7.958 7.958 0 0 0 12 20C15.31 20 18.17 17.78 19.22 14.6L21.26 15.25A10.958 10.958 0 0 1 12 22C8.69 22 5.76 20.35 3.76 17.66L2 19.42V14H7.58L6.35 17.65Z" />
            </svg>
            Refresh
          </button>

          <button className="show_leads_export_btn" onClick={exportLeads}>
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
            </svg>
            Export
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      {uiState.showStats && (
        <div className="show_leads_stats_grid">
          <div className="show_leads_stat_card">
            <div className="show_leads_stat_icon total">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z" />
              </svg>
            </div>
            <div className="show_leads_stat_content">
              <span className="show_leads_stat_value">{stats.total}</span>
              <span className="show_leads_stat_label">Total Leads</span>
            </div>
          </div>

          <div className="show_leads_stat_card">
            <div className="show_leads_stat_icon fashion">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M12,2L13.09,8.26L22,9L17,14L18.18,22.82L12,19.77L5.82,22.82L7,14L2,9L10.91,8.26L12,2Z" />
              </svg>
            </div>
            <div className="show_leads_stat_content">
              <span className="show_leads_stat_value">{stats.fashion}</span>
              <span className="show_leads_stat_label">Fashion Brands</span>
            </div>
          </div>

          <div className="show_leads_stat_card">
            <div className="show_leads_stat_icon startup">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20A8,8 0 0,0 20,12A8,8 0 0,0 12,4M12,6A6,6 0 0,1 18,12A6,6 0 0,1 12,18A6,6 0 0,1 6,12A6,6 0 0,1 12,6Z" />
              </svg>
            </div>
            <div className="show_leads_stat_content">
              <span className="show_leads_stat_value">{stats.startups}</span>
              <span className="show_leads_stat_label">Startups</span>
            </div>
          </div>

          <div className="show_leads_stat_card">
            <div className="show_leads_stat_icon discovery">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M17,12C17,14.42 15.28,16.44 13,16.9V21H11V16.9C8.72,16.44 7,14.42 7,12C7,9.58 8.72,7.56 11,7.1V3H13V7.1C15.28,7.56 17,9.58 17,12M12,9A3,3 0 0,0 9,12A3,3 0 0,0 12,15A3,3 0 0,0 15,12A3,3 0 0,0 12,9Z" />
              </svg>
            </div>
            <div className="show_leads_stat_content">
              <span className="show_leads_stat_value">
                {stats.discoveryCallYes}
              </span>
              <span className="show_leads_stat_label">Discovery Calls</span>
            </div>
          </div>
        </div>
      )}

      {/* Search and Filter Bar */}
      <div className="show_leads_filter_section">
        <div className="show_leads_search_container">
          <div className="show_leads_search_wrapper">
            <svg
              className="show_leads_search_icon"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M9.5,3A6.5,6.5 0 0,1 16,9.5C16,11.11 15.41,12.59 14.44,13.73L14.71,14H15.5L20.5,19L19,20.5L14,15.5V14.71L13.73,14.44C12.59,15.41 11.11,16 9.5,16A6.5,6.5 0 0,1 3,9.5A6.5,6.5 0 0,1 9.5,3M9.5,5C7,5 5,7 5,9.5C5,12 7,14 9.5,14C12,14 14,12 14,9.5C14,7 12,5 9.5,5Z" />
            </svg>
            <input
              type="text"
              placeholder="Search leads by name, email, company, or phone..."
              value={uiState.searchTerm}
              onChange={handleSearch}
              className="show_leads_search_input"
            />
            {uiState.searchTerm && (
              <button
                className="show_leads_search_clear"
                onClick={() =>
                  setUiState((prev) => ({ ...prev, searchTerm: "" }))
                }
              >
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
                </svg>
              </button>
            )}
          </div>
        </div>

        <button
          className={`show_leads_filter_toggle ${
            uiState.showFilters ? "active" : ""
          }`}
          onClick={() =>
            setUiState((prev) => ({ ...prev, showFilters: !prev.showFilters }))
          }
        >
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M14,12V19.88C14.04,20.18 13.94,20.5 13.71,20.71C13.32,21.1 12.69,21.1 12.3,20.71L10.29,18.7C10.06,18.47 9.96,18.16 10,17.87V12H9.97L4.21,4.62C3.87,4.19 3.95,3.56 4.38,3.22C4.57,3.08 4.78,3 5,3V3H19V3C19.22,3 19.43,3.08 19.62,3.22C20.05,3.56 20.13,4.19 19.79,4.62L14.03,12H14Z" />
          </svg>
          Filters
        </button>
      </div>

      {/* Advanced Filters */}
      {uiState.showFilters && (
        <div className="show_leads_advanced_filters">
          <div className="show_leads_filter_row">
            <div className="show_leads_filter_group">
              <label>User Type</label>
              <select
                value={uiState.filterBy.userType}
                className="form-control"
                onChange={(e) => handleFilterChange("userType", e.target.value)}
              >
                <option value="">All Types</option>
                {getUniqueValues("userType").map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </div>

            <div className="show_leads_filter_group">
              <label>Brand Interest</label>
              <select
                value={uiState.filterBy.brandInterest}
                className="form-control"
                onChange={(e) => handleFilterChange("brandInterest", e.target.value)}
              >
                <option value="">All</option>
                {getUniqueValues("brandInterest").map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </div>

            <div className="show_leads_filter_group">
              <label>Creator Interest</label>
              <select
                value={uiState.filterBy.creatorInterest}
                className="form-control"
                onChange={(e) => handleFilterChange("creatorInterest", e.target.value)}
              >
                <option value="">All</option>
                {getUniqueValues("creatorInterest").map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </div>

            <div className="show_leads_filter_group">
              <label>Partner Interest</label>
              <select
                value={uiState.filterBy.partnerInterest}
                className="form-control"
                onChange={(e) => handleFilterChange("partnerInterest", e.target.value)}
              >
                <option value="">All</option>
                {getUniqueValues("partnerInterest").map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </div>

            <div className="show_leads_filter_group">
              <label>Timeline</label>
              <select
                value={uiState.filterBy.timeline}
                className="form-control"
                onChange={(e) => handleFilterChange("timeline", e.target.value)}
              >
                <option value="">All Timelines</option>
                {getUniqueValues("timeline").map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </div>

            <div className="show_leads_filter_group">
              <label>Start Date</label>
              <input
                type="date"
                className="form-control"
                value={uiState.dateRange.startDate}
                onChange={(e) => handleDateRangeChange("startDate", e.target.value)}
              />
            </div>

            <div className="show_leads_filter_group">
              <label>End Date</label>
              <input
                type="date"
                className="form-control"
                value={uiState.dateRange.endDate}
                onChange={(e) => handleDateRangeChange("endDate", e.target.value)}
              />
            </div>

            <button
              className="show_leads_clear_filters"
              onClick={clearAllFilters}
            >
              Clear All
            </button>
          </div>
        </div>
      )}

      {/* Error Message */}
      {uiState.error && (
        <div className="show_leads_error">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M13,13H11V7H13M13,17H11V15H13M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z" />
          </svg>
          <span>{uiState.error}</span>
          <button
            onClick={() => setUiState((prev) => ({ ...prev, error: null }))}
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
            </svg>
          </button>
        </div>
      )}

      {/* Results Info */}
      <div className="show_leads_results_info">
        <span className="show_leads_results_count">
          Showing {leadsData.preferences.length} of {leadsData.total} leads
          {uiState.loading && (
            <span className="show_leads_loading_text"> (Loading...)</span>
          )}
        </span>

        {uiState.selectedLeads.size > 0 && (
          <div className="show_leads_selection_info">
            <span>{uiState.selectedLeads.size} selected</span>
            <button
              onClick={() =>
                setUiState((prev) => ({ ...prev, selectedLeads: new Set() }))
              }
            >
              Clear selection
            </button>
          </div>
        )}
      </div>

      {/* Leads Table */}
      <div className="show_leads_table_container">
        <table className="show_leads_table">
          <thead>
            <tr>
              <th className="sortable" onClick={() => handleSort("firstName")}>
                Name
                <svg className="sort-icon" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M13,20H11V8L5.5,13.5L4.08,12.08L12,4.16L19.92,12.08L18.5,13.5L13,8V20Z" />
                </svg>
              </th>

              <th className="sortable" onClick={() => handleSort("company")}>
                Company
                <svg className="sort-icon" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M13,20H11V8L5.5,13.5L4.08,12.08L12,4.16L19.92,12.08L18.5,13.5L13,8V20Z" />
                </svg>
              </th>

              <th>Contact</th>

              <th>User Type</th>

              <th>Timeline</th>

              <th className="sortable" onClick={() => handleSort("createdAt")}>
                Date Created
                <svg className="sort-icon" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M13,20H11V8L5.5,13.5L4.08,12.08L12,4.16L19.92,12.08L18.5,13.5L13,8V20Z" />
                </svg>
              </th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {leadsData.preferences.map((lead) => {
              const n = normalizeLead(lead);
              return (
                <tr key={n.id || `${n.email}-${n.createdAt}`}>
                  <td>
                    <div className="show_leads_name_cell">
                      <div className="show_leads_avatar">
                        {(n.firstName?.[0] || "").toUpperCase()}
                      </div>
                      <div className="show_leads_name_info">
                        <span className="show_leads_full_name">
                          {n.firstName} {n.lastName}
                        </span>
                      </div>
                    </div>
                  </td>

                  <td>
                    <span className="show_leads_company">{n.company}</span>
                  </td>

                  <td>
                    <div className="show_leads_contact_info">
                      <div className="show_leads_email">{n.email}</div>
                      <div className="show_leads_phone">{n.phone}</div>
                    </div>
                  </td>

                  <td>
                    <span className={`show_leads_badge show_leads_badge_${getBadgeColor("userType", n.userType)}`}>
                      {n.userType || "—"}
                    </span>
                  </td>

                  <td>
                    <span className={`show_leads_badge show_leads_badge_${getBadgeColor("timeline", n.timeline)}`}>{n.timeline || "—"}</span>
                  </td>

                  <td>
                    <span className="show_leads_date">{formatDate(n.createdAt)}</span>
                  </td>

                  <td>
                    <div className="show_leads_actions">
                      <button className="show_leads_action_btn view" onClick={() => showLeadDetails(n)} title="View Details">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12,9A3,3 0 0,0 9,12A3,3 0 0,0 12,15A3,3 0 0,0 15,12A3,3 0 0,0 12,9M12,17A5,5 0 0,1 7,12A5,5 0 0,1 12,7A5,5 0 0,1 17,12A5,5 0 0,1 12,17M12,4.5C7,4.5 2.73,7.61 1,12C2.73,16.39 7,19.5 12,19.5C17,19.5 21.27,16.39 23,12C21.27,7.61 17,4.5 12,4.5Z" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {leadsData.preferences.length === 0 && !uiState.loading && (
          <div className="show_leads_empty_state">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4M12,6A6,6 0 0,0 6,12A6,6 0 0,0 12,18A6,6 0 0,0 18,12A6,6 0 0,0 12,6Z" />
            </svg>
            <h3>No leads found</h3>
            <p>Try adjusting your search or filter criteria</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {leadsData.totalPages > 1 && (
        <div className="show_leads_pagination">
          <button
            disabled={uiState.currentPage === 1 || uiState.loading}
            onClick={() => fetchLeads(uiState.currentPage - 1)}
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M15.41,7.41L14,6L8,12L14,18L15.41,16.59L10.83,12L15.41,7.41Z" />
            </svg>
            Previous
          </button>

          <div className="show_leads_page_numbers">
            {[...Array(Math.min(leadsData.totalPages, 10))].map((_, index) => {
              let pageNumber;
              if (leadsData.totalPages <= 10) {
                pageNumber = index + 1;
              } else {
                const current = uiState.currentPage;
                const total = leadsData.totalPages;
                if (current <= 5) {
                  pageNumber = index + 1;
                } else if (current >= total - 4) {
                  pageNumber = total - 9 + index;
                } else {
                  pageNumber = current - 4 + index;
                }
              }

              return (
                <button
                  key={pageNumber}
                  className={uiState.currentPage === pageNumber ? "active" : ""}
                  onClick={() => fetchLeads(pageNumber)}
                  disabled={uiState.loading}
                >
                  {pageNumber}
                </button>
              );
            })}
          </div>

          <button
            disabled={
              uiState.currentPage === leadsData.totalPages || uiState.loading
            }
            onClick={() => fetchLeads(uiState.currentPage + 1)}
          >
            Next
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M8.59,16.59L10,18L16,12L10,6L8.59,7.41L13.17,12L8.59,16.59Z" />
            </svg>
          </button>
        </div>
      )}

      {/* Detail Modal (show only requested fields when present) */}
      {uiState.showDetailModal && uiState.selectedLead && (
        <div className="show_leads_modal_overlay" onClick={closeDetailModal}>
          <div className="show_leads_modal" onClick={(e) => e.stopPropagation()}>
            <div className="show_leads_modal_header">
              <h2>Lead Details</h2>
              <button className="show_leads_modal_close" onClick={closeDetailModal}>
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
                </svg>
              </button>
            </div>

            <div className="show_leads_modal_content">
              {/* Personal Information */}
              <div className="show_leads_modal_section">
                <h3>Personal Information</h3>
                <div className="show_leads_modal_grid">
                  <div className="show_leads_modal_field">
                    <label>Name</label>
                    <span>{uiState.selectedLead.firstName} {uiState.selectedLead.lastName}</span>
                  </div>

                  {uiState.selectedLead.email && (
                    <div className="show_leads_modal_field">
                      <label>Email</label>
                      <span>{uiState.selectedLead.email}</span>
                    </div>
                  )}

                  {uiState.selectedLead.phone && (
                    <div className="show_leads_modal_field">
                      <label>Phone</label>
                      <span>{uiState.selectedLead.phone}</span>
                    </div>
                  )}

                  {uiState.selectedLead.company && (
                    <div className="show_leads_modal_field">
                      <label>Company</label>
                      <span>{uiState.selectedLead.company}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* User & Timeline */}
              <div className="show_leads_modal_section">
                <h3>User & Timeline</h3>
                <div className="show_leads_modal_grid">
                  {uiState.selectedLead.userType && (
                    <div className="show_leads_modal_field">
                      <label>User Type</label>
                      <span>{uiState.selectedLead.userType}</span>
                    </div>
                  )}

                  {uiState.selectedLead.userTypeOther && (
                    <div className="show_leads_modal_field">
                      <label>User Type (Other)</label>
                      <span>{uiState.selectedLead.userTypeOther}</span>
                    </div>
                  )}

                  {uiState.selectedLead.timeline && (
                    <div className="show_leads_modal_field">
                      <label>Timeline</label>
                      <span>{uiState.selectedLead.timeline}</span>
                    </div>
                  )}

                  {uiState.selectedLead.timelineOther && (
                    <div className="show_leads_modal_field">
                      <label>Timeline (Other)</label>
                      <span>{uiState.selectedLead.timelineOther}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Interests */}
              <div className="show_leads_modal_section">
                <h3>Interests</h3>
                <div className="show_leads_modal_grid">
                  {uiState.selectedLead.brandInterest && (
                    <div className="show_leads_modal_field">
                      <label>Brand Interest</label>
                      <span>{uiState.selectedLead.brandInterest}</span>
                    </div>
                  )}

                  {uiState.selectedLead.brandInterestOther && (
                    <div className="show_leads_modal_field">
                      <label>Brand Interest (Other)</label>
                      <span>{uiState.selectedLead.brandInterestOther}</span>
                    </div>
                  )}

                  {uiState.selectedLead.creatorInterest && (
                    <div className="show_leads_modal_field">
                      <label>Creator Interest</label>
                      <span>{uiState.selectedLead.creatorInterest}</span>
                    </div>
                  )}

                  {uiState.selectedLead.creatorInterestOther && (
                    <div className="show_leads_modal_field">
                      <label>Creator Interest (Other)</label>
                      <span>{uiState.selectedLead.creatorInterestOther}</span>
                    </div>
                  )}

                  {uiState.selectedLead.partnerInterest && (
                    <div className="show_leads_modal_field">
                      <label>Partner Interest</label>
                      <span>{uiState.selectedLead.partnerInterest}</span>
                    </div>
                  )}

                  {uiState.selectedLead.partnerInterestOther && (
                    <div className="show_leads_modal_field">
                      <label>Partner Interest (Other)</label>
                      <span>{uiState.selectedLead.partnerInterestOther}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Other Details */}
              <div className="show_leads_modal_section">
                <h3>Other Details</h3>
                <div className="show_leads_modal_grid">
                  {uiState.selectedLead.interest?.length > 0 && (
                    <div className="show_leads_modal_field">
                      <label>Interests (list)</label>
                      <div className="show_leads_tags">
                        {uiState.selectedLead.interest.map((it, i) => (
                          <span key={i} className="show_leads_tag">{it}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {uiState.selectedLead.challenges?.length > 0 && (
                    <div className="show_leads_modal_field">
                      <label>Challenges</label>
                      <div className="show_leads_tags">
                        {uiState.selectedLead.challenges.map((ch, i) => (
                          <span key={i} className="show_leads_tag challenge">{ch}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {uiState.selectedLead.short_term_goals && (
                    <div className="show_leads_modal_field">
                      <label>Short-term Goals</label>
                      <span>{uiState.selectedLead.short_term_goals}</span>
                    </div>
                  )}

                  {uiState.selectedLead.discovery_call && (
                    <div className="show_leads_modal_field">
                      <label>Discovery Call</label>
                      <span>{uiState.selectedLead.discovery_call}</span>
                    </div>
                  )}

                  {uiState.selectedLead.referral_source && (
                    <div className="show_leads_modal_field">
                      <label>Referral Source</label>
                      <span>{uiState.selectedLead.referral_source}</span>
                    </div>
                  )}

                  {/* Always show timestamps */}
                  <div className="show_leads_modal_field">
                    <label>Created</label>
                    <span>{formatDate(uiState.selectedLead.createdAt)}</span>
                  </div>
                  <div className="show_leads_modal_field">
                    <label>Last Updated</label>
                    <span>{formatDate(uiState.selectedLead.updatedAt)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShowLeads;
